import React, { useState, useEffect } from 'react';

const EventAdmin = ({ page = 'event_list', onNavigate }) => {
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // イベント作成
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', location: '', memo: '' });

  // イベント詳細
  const [selectedEvent, setSelectedEvent] = useState(null);

  // インポート
  const [importPreview, setImportPreview] = useState(null);

  // 参加者追加
  const [addMemberSearch, setAddMemberSearch] = useState('');

  // 参加者編集モーダル
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [seminars, setSeminars] = useState([]);

  useEffect(() => {
    fetch('/data/events.json?t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setEvents).catch(() => setEvents([]));
    fetch('/data/customers.json?t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setCustomers).catch(() => setCustomers([]));
    fetch('/data/seminars.json?t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setSeminars).catch(() => setSeminars([]));
  }, []);

  const saveEvents = async (data) => {
    setSaving(true);
    try {
      const res = await fetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'events', data }) });
      const result = await res.json();
      if (result.success) setEvents(data);
    } catch {}
    setSaving(false);
  };

  const saveCustomers = async (data) => {
    try {
      const res = await fetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'customers', data }) });
      const result = await res.json();
      if (result.success) setCustomers(data);
    } catch {}
  };

  const getField = (c, key) => (c.fields || []).find((f) => f.key === key)?.value || '';

  const createEvent = () => {
    if (!form.name.trim()) return;
    const newEvent = { id: String(Date.now()), ...form, createdAt: new Date().toISOString() };
    saveEvents([...events, newEvent]);
    setForm({ name: '', date: '', location: '', memo: '' });
    setShowForm(false);
    setMessage(`「${newEvent.name}」を作成しました`);
  };

  const deleteEvent = (id) => {
    if (!window.confirm('このイベントを削除しますか？')) return;
    saveEvents(events.filter((e) => e.id !== id));
    if (selectedEvent?.id === id) setSelectedEvent(null);
    setMessage('削除しました');
  };

  // イベントに紐づく顧客
  const getEventCustomers = (eventId) => customers.filter((c) => getField(c, 'イベントID') === eventId);

  // イベント内のMLパターン一覧
  const getEventPatterns = (eventId) => {
    const members = getEventCustomers(eventId);
    const map = {};
    members.forEach((c) => {
      const p = getField(c, 'MLパターン');
      if (p) {
        if (!map[p]) map[p] = 0;
        map[p]++;
      }
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  };

  // Excelインポート
  const handleExcelFile = async (e, eventId) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('action', 'preview');
    formData.append('eventId', eventId);
    try {
      const res = await fetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setImportPreview({ data: data.parsed, totalCount: data.totalCount, skippedList: data.skipped, fileName: file.name, eventId, file });
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    e.target.value = '';
  };

  const executeImport = async () => {
    if (!importPreview) return;
    const formData = new FormData();
    formData.append('file', importPreview.file);
    formData.append('action', 'import');
    formData.append('eventId', importPreview.eventId);
    try {
      const res = await fetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.importedCount}件をインポートしました${data.skippedCount > 0 ? `（重複スキップ: ${data.skippedCount}件）` : ''}`);
        fetch('/data/customers.json?t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setCustomers);
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    setImportPreview(null);
  };

  // 参加者手動追加
  const addMemberToEvent = async () => {
    if (!addMemberSearch.trim()) return;
    const email = addMemberSearch.trim();
    if (!email.includes('@')) return;

    const currentId = localStorage.getItem('selected_event_id') || '';
    const existing = customers.find((c) => c.email?.toLowerCase() === email.toLowerCase());

    if (existing) {
      // 既存顧客にイベントIDを紐づけ
      const hasEvent = (existing.fields || []).some((f) => f.key === 'イベントID' && f.value === currentId);
      if (!hasEvent) {
        const updated = customers.map((c) => {
          if (c.id !== existing.id) return c;
          return { ...c, fields: [...(c.fields || []), { key: 'イベントID', value: currentId }] };
        });
        await saveCustomers(updated);
      }
    } else {
      // 新規顧客登録+イベントID紐づけ
      const newCustomer = {
        id: String(Date.now()),
        name: '',
        email,
        type: 'company',
        status: '見込み',
        memo: '',
        fields: [{ key: 'イベントID', value: currentId }],
      };
      await saveCustomers([...customers, newCustomer]);
    }

    setAddMemberSearch('');
    setMessage('追加しました');
  };

  // 参加者編集
  const openEdit = (customer) => {
    setEditForm({ ...customer, fields: [...(customer.fields || [])] });
  };

  const saveEdit = async () => {
    if (!editForm) return;
    setEditSaving(true);
    const updated = customers.map((c) => c.id === editForm.id ? editForm : c);
    await saveCustomers(updated);
    setEditForm(null);
    setMessage('保存しました');
    setEditSaving(false);
  };

  const statusOptions = ['見込み', '商談中', '契約済み', '休止'];
  const statusColors = {
    '見込み': 'bg-yellow-100 text-yellow-700',
    '商談中': 'bg-blue-100 text-blue-700',
    '契約済み': 'bg-green-100 text-green-700',
    '休止': 'bg-gray-100 text-gray-500',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP');
  };

  // ===== イベント一覧 =====
  if (page === 'event_list') {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">イベント一覧</h2>
          <button onClick={() => setShowForm(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">+ 新規イベント</button>
        </div>

        {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

        {showForm && (
          <div className="bg-white rounded-xl shadow p-6 mb-4 space-y-3">
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="イベント名（例: 働き方の祭典2026）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="開催場所" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={2}
              placeholder="メモ（任意）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            <div className="flex gap-3">
              <button onClick={createEvent} disabled={saving || !form.name.trim()} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">作成</button>
              <button onClick={() => { setShowForm(false); setForm({ name: '', date: '', location: '', memo: '' }); }} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
            </div>
          </div>
        )}

        {/* セミナー（自社主催） */}
        {seminars.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 mb-2">セミナー（自社主催）</h3>
            <div className="space-y-2">
              {seminars.map((sem, i) => {
                const semId = 'seminar_' + (sem.id || i);
                const semMembers = getEventCustomers(semId);
                return (
                  <div key={i} className="bg-white rounded-xl shadow p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-sm">{sem.fullTitle || sem.shortTitle || sem.label}</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700">セミナー</span>
                      </div>
                      <div className="flex gap-3 mt-1 text-xs text-gray-500">
                        {sem.eventDate && <span>{sem.eventDate}</span>}
                        {sem.time && <span>{sem.time}</span>}
                        <span>{semMembers.length}名</span>
                      </div>
                    </div>
                    <button onClick={() => { localStorage.setItem('selected_event_id', semId); if (onNavigate) onNavigate('event_members'); }}
                      className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200">参加者管理</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* イベント（外部参加等） */}
        <div>
          <h3 className="text-sm font-bold text-gray-500 mb-2">イベント</h3>
          {events.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">イベントがありません</div>
          ) : (
            <div className="space-y-2">
              {[...events].reverse().map((ev) => {
                const members = getEventCustomers(ev.id);
                return (
                  <div key={ev.id} className="bg-white rounded-xl shadow p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-sm">{ev.name}</h3>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-green-100 text-green-700">イベント</span>
                        </div>
                        <div className="flex gap-3 mt-1 text-xs text-gray-500">
                          {ev.date && <span>{formatDate(ev.date)}</span>}
                          {ev.location && <span>{ev.location}</span>}
                          <span>{members.length}名</span>
                        </div>
                        {ev.memo && <p className="text-xs text-gray-400 mt-1">{ev.memo}</p>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { localStorage.setItem('selected_event_id', ev.id); if (onNavigate) onNavigate('event_members'); }}
                          className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200">参加者管理</button>
                        <button onClick={() => deleteEvent(ev.id)} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100">削除</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== 参加者管理 =====
  if (page === 'event_members') {
    const savedId = localStorage.getItem('selected_event_id');
    // セミナー+イベントを統合した選択肢
    const allItems = [
      ...seminars.map((sem, i) => ({ id: 'seminar_' + (sem.id || i), name: sem.fullTitle || sem.shortTitle || sem.label, type: 'seminar' })),
      ...events.map((e) => ({ id: e.id, name: e.name, type: 'event', date: e.date })),
    ];
    const currentId = selectedEvent?.id || savedId || (allItems.length > 0 ? allItems[allItems.length - 1].id : '');
    const currentItem = allItems.find((item) => item.id === currentId) || allItems[allItems.length - 1];
    const members = currentId ? getEventCustomers(currentId) : [];

    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">参加者管理</h2>
            {currentItem && <p className="text-sm text-gray-500 mt-1">{currentItem.name}</p>}
          </div>
          <div className="flex gap-2">
              <>
                <select
                  value={currentId}
                  onChange={(e) => { localStorage.setItem('selected_event_id', e.target.value); setSelectedEvent({ id: e.target.value }); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {seminars.length > 0 && <optgroup label="セミナー">
                    {seminars.map((sem, i) => <option key={'s'+i} value={'seminar_' + (sem.id || i)}>{sem.fullTitle || sem.shortTitle || sem.label}</option>)}
                  </optgroup>}
                  {events.length > 0 && <optgroup label="イベント">
                    {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </optgroup>}
                </select>
                <label className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700 cursor-pointer">
                  Excelインポート
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => handleExcelFile(e, currentId)} />
                </label>
              </>
          </div>
        </div>

        {/* 手動追加 */}
        {currentId && (
          <div className="mb-4 bg-white rounded-xl shadow px-4 py-3">
            <div className="flex gap-2">
              <input type="text" value={addMemberSearch} onChange={(e) => setAddMemberSearch(e.target.value)}
                placeholder="名前・メール・会社名で検索して追加、またはメールアドレスを直接入力"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && addMemberToEvent()} />
              <button onClick={addMemberToEvent} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">追加</button>
            </div>
            {addMemberSearch.trim() && !addMemberSearch.includes('@') && (() => {
              const s = addMemberSearch.toLowerCase();
              const memberEmails = new Set(members.map((c) => c.email));
              const matches = customers.filter((c) => c.email && !memberEmails.has(c.email) &&
                (c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || getField(c, '会社名').toLowerCase().includes(s))
              ).slice(0, 8);
              return matches.length > 0 ? (
                <div className="mt-2 border border-gray-200 rounded bg-white max-h-40 overflow-y-auto">
                  {matches.map((c) => (
                    <button key={c.id} onClick={() => setAddMemberSearch(c.email)}
                      className="w-full text-left px-3 py-1.5 hover:bg-blue-50 text-xs flex items-center justify-between border-b last:border-b-0">
                      <span><span className="font-bold">{c.name}</span> <span className="text-gray-400">{getField(c, '会社名')}</span></span>
                      <span className="text-gray-400">{c.email}</span>
                    </button>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        )}

        {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

        {/* インポートプレビュー */}
        {importPreview && (
          <div className="mb-4 bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-lg mb-3">インポートプレビュー: {importPreview.fileName}</h3>
            <div className="flex gap-4 mb-3 text-sm">
              <span className="font-bold text-blue-700">取り込み: {(importPreview.totalCount || importPreview.data.length)}件</span>
              {importPreview.skippedList.length > 0 && <span className="font-bold text-orange-600">重複スキップ: {importPreview.skippedList.length}件</span>}
            </div>
            <div className="max-h-60 overflow-y-auto border rounded-lg mb-4">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">名前</th>
                    <th className="text-left px-3 py-2">メール</th>
                    <th className="text-left px-3 py-2">会社名</th>
                    <th className="text-left px-3 py-2">MLパターン</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {importPreview.data.slice(0, 50).map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5">{c.name}</td>
                      <td className="px-3 py-1.5">{c.email}</td>
                      <td className="px-3 py-1.5">{(c.fields || []).find((f) => f.key === '会社名')?.value || ''}</td>
                      <td className="px-3 py-1.5">{(c.fields || []).find((f) => f.key === 'MLパターン')?.value || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(importPreview.totalCount || importPreview.data.length) > 50 && <p className="text-center text-xs text-gray-400 py-2">他 {(importPreview.totalCount || importPreview.data.length) - 50}件...</p>}
            </div>
            {importPreview.skippedList.length > 0 && (
              <details className="mb-4">
                <summary className="text-sm font-bold text-orange-600 cursor-pointer hover:underline">スキップされた {importPreview.skippedList.length}件を表示</summary>
                <div className="mt-2 max-h-40 overflow-y-auto border border-orange-200 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-orange-50 sticky top-0"><tr><th className="text-left px-3 py-2">名前</th><th className="text-left px-3 py-2">メール</th><th className="text-left px-3 py-2">理由</th></tr></thead>
                    <tbody className="divide-y">{importPreview.skippedList.map((s, i) => (<tr key={i}><td className="px-3 py-1.5">{s.name}</td><td className="px-3 py-1.5">{s.email}</td><td className="px-3 py-1.5 text-orange-600">{s.reason}</td></tr>))}</tbody>
                  </table>
                </div>
              </details>
            )}
            <div className="flex gap-3">
              <button onClick={executeImport} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'インポート中...' : `${(importPreview.totalCount || importPreview.data.length)}件をインポート`}
              </button>
              <button onClick={() => setImportPreview(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
            </div>
          </div>
        )}

        {!currentId ? (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">セミナーまたはイベントを選択してください</div>
        ) : (
          <>
            {/* 参加者テーブル */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="max-h-[65vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">名前</th>
                      <th className="text-left px-3 py-2">会社名</th>
                      <th className="text-left px-3 py-2">メール</th>
                      <th className="text-left px-3 py-2">ステータス</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {members.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openEdit(c)}>
                        <td className="px-3 py-2 text-blue-600 hover:underline">{c.name}</td>
                        <td className="px-3 py-2 text-gray-500">{getField(c, '会社名')}</td>
                        <td className="px-3 py-2 text-gray-500">{c.email}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusColors[c.status] || 'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* 編集モーダル */}
        {editForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto">
              <h3 className="font-bold text-lg mb-4">参加者情報の編集</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold mb-1">姓</label>
                    <input type="text" value={(editForm.name || '').split(/\s+/)[0] || ''} onChange={(e) => {
                      const mei = (editForm.name || '').split(/\s+/).slice(1).join(' ');
                      setEditForm({ ...editForm, name: (e.target.value + ' ' + mei).trim() });
                    }} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">名</label>
                    <input type="text" value={(editForm.name || '').split(/\s+/).slice(1).join(' ') || ''} onChange={(e) => {
                      const sei = (editForm.name || '').split(/\s+/)[0] || '';
                      setEditForm({ ...editForm, name: (sei + ' ' + e.target.value).trim() });
                    }} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">メールアドレス</label>
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold mb-1">区分</label>
                    <select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="company">企業</option><option value="sr">社労士</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">ステータス</label>
                    <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">MLパターン</label>
                    <select value={(editForm.fields || []).find((f) => f.key === 'MLパターン')?.value || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditForm((prev) => {
                          const fields = prev.fields.filter((f) => f.key !== 'MLパターン');
                          if (val) fields.push({ key: 'MLパターン', value: val });
                          return { ...prev, fields };
                        });
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">なし</option>
                      {currentId && getEventPatterns(currentId).map(([p]) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">メモ</label>
                  <textarea value={editForm.memo} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })} rows={2}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={saveEdit} disabled={editSaving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                    {editSaving ? '保存中...' : '保存'}
                  </button>
                  <button onClick={() => setEditForm(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default EventAdmin;
